import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useClub } from '../../hooks/useClub';
import MemberTimeline from './MemberTimeline';

function interpolateScript(body, memberName, repName, clubName) {
  if (!body) return body;
  return body
    .replace(/\[Name\]/g, (() => { const n = memberName?.split(' ')[0]; return n ? n.charAt(0).toUpperCase() + n.slice(1) : '[Name]'; })())
    .replace(/\[Rep Name\]/g, repName || '[Rep Name]')
    .replace(/\[Club Name\]/g, clubName || '[Club Name]');
}

// ─── CRM Design Section 3 — T2M Stage Outcome Taxonomy ───────────────────────
// Stages 8-9: Daily Check-in | Trial Ask | Post-Trial Follow-Up

const STAGE_CONFIG = {
  checkin: {
    label: 'Daily Check-in',
    sublabel: 'Stages 8–9 · Nurturing',
    color: '#3b82f6',
    outcomes: [
      { value: 'On Track',           category: 'interested',  followUpRequired: true,  whyRequired: false, whyOptional: false },
      { value: 'At Risk',            category: 'interested',  followUpRequired: true,  whyRequired: false, whyOptional: true  },
      { value: 'Intervention Made',  category: 'converted',   followUpRequired: true,  whyRequired: false, whyOptional: true  },
      { value: 'No Answer',          category: 'no_answer',   followUpRequired: true,  whyRequired: false, whyOptional: false },
      { value: 'Left Voicemail',     category: 'no_answer',   followUpRequired: true,  whyRequired: false, whyOptional: false },
    ],
  },
  trial_ask: {
    label: 'Trial Ask',
    sublabel: 'Close Attempt',
    color: '#f97316',
    outcomes: [
      { value: 'Sold Membership',    category: 'converted',   followUpRequired: true,  whyRequired: false, whyOptional: false, needsPlan: true  },
      { value: 'Interested',         category: 'interested',  followUpRequired: true,  whyRequired: false, whyOptional: true  },
      { value: 'Needs Manager',      category: 'interested',  followUpRequired: true,  whyRequired: false, whyOptional: true  },
      { value: 'Soft No',            category: 'soft_no',     followUpRequired: true,  whyRequired: true,  whyOptional: false },
      { value: 'Hard No',            category: 'hard_no',     followUpRequired: false, whyRequired: true,  whyOptional: false },
      { value: 'Failed to Ask',      category: 'no_answer',   followUpRequired: true,  whyRequired: false, whyOptional: true  },
    ],
  },
  post_trial: {
    label: 'Post-Trial Follow-Up',
    sublabel: 'After Trial Ends',
    color: '#a855f7',
    outcomes: [
      { value: 'Converted',          category: 'converted',   followUpRequired: true,  whyRequired: false, whyOptional: false, needsPlan: true  },
      { value: 'Extended Trial',     category: 'interested',  followUpRequired: true,  whyRequired: false, whyOptional: true  },
      { value: 'Left Voicemail',     category: 'no_answer',   followUpRequired: true,  whyRequired: false, whyOptional: false },
      { value: 'No Answer',          category: 'no_answer',   followUpRequired: true,  whyRequired: false, whyOptional: false },
      { value: 'Re-Engage',          category: 're_engage',   followUpRequired: true,  whyRequired: false, whyOptional: true  },
      { value: 'Soft No',            category: 'soft_no',     followUpRequired: true,  whyRequired: true,  whyOptional: false },
      { value: 'Hard No',            category: 'hard_no',     followUpRequired: false, whyRequired: true,  whyOptional: false },
    ],
  },
};

const OUTCOME_CATEGORY_COLORS = {
  converted:  { bg: '#22c55e15', border: '#22c55e', text: '#22c55e' },
  interested: { bg: '#3b82f615', border: '#3b82f6', text: '#60a5fa' },
  no_answer:  { bg: '#6b728015', border: '#6b7280', text: '#9ca3af' },
  soft_no:    { bg: '#f9731615', border: '#f97316', text: '#f97316' },
  hard_no:    { bg: '#ef444415', border: '#ef4444', text: '#ef4444' },
  re_engage:  { bg: '#a855f715', border: '#a855f7', text: '#a855f7' },
};

const WHY_PLACEHOLDERS = {
  soft_no:  'e.g. Price, timing, not ready, travel, prefers drop-in...',
  hard_no:  'e.g. Not interested, moving away, DNC...',
  default:  'Additional context...',
};

const PLAN_TYPES_BASE = [
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount',
  'Unlimited Club Only — 20% Discount',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Trial Extension',
];

const PLAN_TYPES_DEN = [
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount',
  'Unlimited Club Only — 20% Discount',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Summer Slam — $49/mo Daytime Club',
  'Summer Slam — $65/mo Social',
  'Summer Slam — $79/mo Adult Unlimited',
  'Trial Extension',
];

const VERIFICATION_METHODS = [
  'ID + Phone',
  'ID Only',
  'Phone Only',
  'Manager Override',
  'Attempted — No Answer',
];

// ─── Quick picks & helpers for follow-up scheduler ───────────────────────────
const QUICK_PICKS = [
  { label: 'Today',     days: 0 },
  { label: 'Tomorrow',  days: 1 },
  { label: 'In 2 days', days: 2 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
];

const TIME_OPTIONS = [
  '8:00 AM','8:30 AM','9:00 AM','9:30 AM','10:00 AM','10:30 AM',
  '11:00 AM','11:30 AM','12:00 PM','12:30 PM','1:00 PM','1:30 PM',
  '2:00 PM','2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM',
  '5:00 PM','5:30 PM','6:00 PM','6:30 PM','7:00 PM','7:30 PM','8:00 PM',
];

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function formatDateISO(date) {
  return date.toISOString().split('T')[0];
}

function formatDateDisplay(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// ─── Mini Calendar ────────────────────────────────────────────────────────────
function MiniCalendar({ selectedDate, minDate, onSelect }) {
  const today = new Date();
  const init  = selectedDate ? new Date(selectedDate + 'T12:00:00') : today;
  const [viewYear,  setViewYear]  = useState(init.getFullYear());
  const [viewMonth, setViewMonth] = useState(init.getMonth());

  const MONTHS = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December'];
  const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const minISO      = minDate || formatDateISO(today);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 8, padding: 12, userSelect: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', padding: '0 6px' }}>‹</button>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#e5e5e5' }}>{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', padding: '0 6px' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DAYS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 10, color: '#555', fontWeight: 700, padding: '2px 0' }}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const iso        = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isSelected = iso === selectedDate;
          const isToday    = iso === formatDateISO(today);
          const isPast     = iso < minISO;
          return (
            <button
              key={iso}
              disabled={isPast}
              onClick={() => !isPast && onSelect(iso)}
              style={{
                background: isSelected ? '#facc15' : isToday ? '#facc1520' : 'transparent',
                color: isSelected ? '#000' : isPast ? '#333' : isToday ? '#facc15' : '#ccc',
                border: isSelected ? 'none' : isToday ? '1px solid #facc1540' : '1px solid transparent',
                borderRadius: 5, padding: '5px 2px', fontSize: 12,
                cursor: isPast ? 'not-allowed' : 'pointer',
                fontWeight: isSelected ? 700 : 400, textAlign: 'center',
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Follow-Up Scheduler ──────────────────────────────────────────────────────
function FollowUpScheduler({ value, onChange, required, outcomeLabel }) {
  const [quickPick,    setQuickPick]    = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const today = new Date();

  function selectQuick(pick) {
    setQuickPick(pick.label);
    setShowCalendar(false);
    const date = formatDateISO(addDays(today, pick.days));
    onChange({ ...value, date });
  }

  function handleCalendarSelect(iso) {
    setShowCalendar(false);
    setQuickPick('custom');
    onChange({ ...value, date: iso });
  }

  const borderColor = required && !value.date ? '#ef4444' : '#facc1530';

  return (
    <div style={{ background: '#0d1117', border: `1px solid ${borderColor}`, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#facc15' }}>📅 Schedule Follow-Up</span>
        {required && <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 700 }}>REQUIRED</span>}
        {!required && <span style={{ fontSize: 10, color: '#666' }}>recommended for {outcomeLabel}</span>}
      </div>

      {/* Quick picks */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {QUICK_PICKS.map(pick => (
          <button
            key={pick.label}
            onClick={() => selectQuick(pick)}
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
              border: `1px solid ${quickPick === pick.label ? '#facc15' : '#333'}`,
              background: quickPick === pick.label ? '#facc1515' : '#111',
              color: quickPick === pick.label ? '#facc15' : '#888',
              fontWeight: quickPick === pick.label ? 700 : 400,
            }}
          >
            {pick.label}
          </button>
        ))}
        <button
          onClick={() => { setQuickPick('custom'); setShowCalendar(p => !p); }}
          style={{
            fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${quickPick === 'custom' ? '#facc15' : '#333'}`,
            background: quickPick === 'custom' ? '#facc1515' : '#111',
            color: quickPick === 'custom' ? '#facc15' : '#888',
          }}
        >
          📅 Pick date
        </button>
      </div>

      {showCalendar && (
        <div style={{ marginBottom: 10 }}>
          <MiniCalendar selectedDate={value.date} minDate={formatDateISO(today)} onSelect={handleCalendarSelect} />
        </div>
      )}

      {value.date && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Date</div>
              <div
                onClick={() => { setQuickPick('custom'); setShowCalendar(p => !p); }}
                style={{ background: '#111', border: '1px solid #facc1540', borderRadius: 5, padding: '6px 8px', fontSize: 12, color: '#facc15', cursor: 'pointer', fontWeight: 600 }}
              >
                📅 {formatDateDisplay(value.date)}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Time</div>
              <select
                value={value.time || '9:00 AM'}
                onChange={e => onChange({ ...value, time: e.target.value })}
                style={{ width: '100%', background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12 }}
              >
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Follow-up note (optional)</div>
            <textarea
              value={value.note || ''}
              onChange={e => onChange({ ...value, note: e.target.value })}
              placeholder="e.g. Said she'd think about it — call back Thursday morning"
              style={{
                width: '100%', background: '#111', border: '1px solid #333',
                color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12,
                resize: 'none', height: 48, fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Contact method rationale ─────────────────────────────────────────────────
const CONTACT_METHOD_WHY = {
  text: "A text is lower friction than a cold call for someone who hasn't had recent personal contact. Higher open rate, no pressure to respond immediately, and gives them a way to engage on their own terms. If no response in 24–48 hours, escalate to a call.",
  call: "This person had a paying relationship with us or is at a critical trial milestone. A call is more personal and signals you're treating them as a valued contact, not mass outreach. Text first can feel impersonal at this relationship level.",
  email: "Email is the lowest-engagement channel for this type of outreach. Use only after text and call have been attempted. Keep it brief with a single clear call to action.",
};

function getT2MContactRationale(trialDay, contactMethod) {
  if (contactMethod === 'call' && trialDay >= 9) {
    return "Day 9+ is an intervention window — a call is required here. Text is too passive for a member who may be at risk. This is a personal connection, not a check-in.";
  }
  if (contactMethod === 'call' && trialDay >= 12) {
    return "Day 12 is the close window — only 2 days left. A call allows you to have a real conversation about membership. Do not text at this stage.";
  }
  if (contactMethod === 'text' && trialDay <= 5) {
    return "Early in the trial, a text is the right first touch. It's welcoming without being pushy. If they don't respond, escalate to a call on the next checkpoint.";
  }
  return CONTACT_METHOD_WHY[contactMethod] || null;
}

// ─── Script Panel (unchanged from original) ───────────────────────────────────
function ScriptPanel({ trialId, trialDay, visits, memberName, repName, clubName }) {
  const { get } = useApi();
  const [script, setScript]         = useState(null);
  const [allScripts, setAllScripts] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [collapsed, setCollapsed]   = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  const [copied, setCopied]         = useState(false);
  const [whyOpen, setWhyOpen]       = useState({});

  useEffect(() => {
    if (!trialId) return;
    get(`/t2m/script/${trialId}${visits != null ? `?visits=${visits}` : ''}`)
      .then(setScript)
      .catch(() => setScript(null))
      .finally(() => setLoading(false));
  }, [trialId, visits]); // eslint-disable-line react-hooks/exhaustive-deps

  // GAP-fix (Sprint 19): T2M had no way to manually switch scripts — only ever
  // showed the single day-based auto-selection with no picker, unlike PB101.
  // Fetch the full T2M script list separately so staff can override when the
  // auto-selected script isn't the right fit for the conversation.
  useEffect(() => {
    get('/scripts?module=t2m')
      .then(data => setAllScripts(data.scripts || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div style={s.shell}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading script…</div></div>
  );
  if (!script || script.noScript) return null;

  const interpolatedBody = interpolateScript(script.body, memberName, repName, clubName);

  function handleCopy() {
    navigator.clipboard.writeText(interpolatedBody).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const methods = Array.isArray(script.contactMethodOrder) && script.contactMethodOrder.length > 0
    ? script.contactMethodOrder
    : null;

  return (
    <div style={s.shell}>
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>📋</span>
          <span style={s.scriptName}>{script.scriptName}</span>
          {script.isClubOverride && (
            <span style={{ background: 'rgba(234,179,8,0.2)', color: 'var(--yellow)', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>CLUB OVERRIDE</span>
          )}
        </div>
        <button style={s.collapseBtn} onClick={() => setCollapsed(v => !v)}>
          {collapsed ? '▾ Show Script' : '▴ Hide Script'}
        </button>
      </div>
      {!collapsed && (
        <>
          {script.whyContext && (
            <div style={s.whyPanel}>
              <div style={s.whyLabel}>💡 WHY THIS SCRIPT</div>
              <div style={s.whyText}>{script.whyContext}</div>
            </div>
          )}
          {methods && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Contact Order</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {methods.map((method, i) => {
                  const isOpen = whyOpen[method];
                  const rationale = getT2MContactRationale(trialDay, method) || CONTACT_METHOD_WHY[method];
                  const icon  = method === 'text' ? '💬' : method === 'call' ? '📞' : '📧';
                  const color = method === 'call' ? '#f97316' : method === 'text' ? '#60a5fa' : 'var(--text-muted)';
                  return (
                    <div key={method} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {i > 0 && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>→</span>}
                        <span style={{ background: `${color}22`, color, border: `1px solid ${color}`, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                          {icon} {method.toUpperCase()}
                        </span>
                        {rationale && (
                          <button
                            style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 4, padding: '1px 6px', fontSize: 9, cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => setWhyOpen(p => ({ ...p, [method]: !p[method] }))}
                          >
                            {isOpen ? 'Less' : 'Why?'}
                          </button>
                        )}
                      </div>
                      {isOpen && rationale && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: 1.5, maxWidth: 340, background: 'var(--surface-3)', padding: '6px 10px', borderRadius: 6 }}>
                          {rationale}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div style={s.body}>{interpolatedBody}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            <button
              style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              onClick={handleCopy}
            >
              {copied ? '✓ Copied' : '📋 Copy Script'}
            </button>
            <button
              style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 5, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}
              onClick={() => {}}
              title="SMS — available in Mass Comms sprint"
            >
              💬 SMS
            </button>
            <button
              style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 5, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}
              onClick={() => setPickerOpen(p => !p)}
            >
              ↕ Switch script
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
              <input type="checkbox" checked={customMode} onChange={e => setCustomMode(e.target.checked)} />
              Used custom script
            </label>
          </div>

          {pickerOpen && (
            <div style={{ marginTop: 8, border: '1px solid #222', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 10px', background: '#1a1a1a', borderBottom: '1px solid #222' }}>
                T2M Scripts — tap to switch
              </div>
              {allScripts.map((s, i) => {
                const isCurrent = s.name === script.scriptName;
                return (
                  <button key={s.script_id || i} onClick={() => {
                    // Normalize the list item (snake_case) into the shape this
                    // panel's render code expects (camelCase, from /t2m/script/:id)
                    setScript({
                      scriptName: s.name,
                      body: s.body,
                      isClubOverride: s.scope === 'club',
                      contactMethodOrder: s.contact_method_order,
                      whyContext: s.why_context,
                    });
                    setPickerOpen(false);
                  }} style={{
                    width: '100%', textAlign: 'left', padding: '7px 10px',
                    background: isCurrent ? 'rgba(234,179,8,0.1)' : '#111',
                    border: 'none', borderBottom: i < allScripts.length - 1 ? '1px solid #1a1a1a' : 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e5e5' }}>{s.name}</div>
                      {s.timing && <div style={{ fontSize: 10, color: '#666', marginTop: 1 }}>{s.timing}</div>}
                    </div>
                    {isCurrent && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#facc15', background: 'rgba(234,179,8,0.15)', padding: '1px 5px', borderRadius: 3 }}>CURRENT</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {customMode && (
            <div style={{ marginTop: 8 }}>
              <textarea
                style={{ width: '100%', background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, padding: '8px', resize: 'vertical', minHeight: 60 }}
                placeholder="Optional: note what you actually said (face-to-face paraphrasing)..."
                value={customText}
                onChange={e => setCustomText(e.target.value)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function OutcomeModal({ trial, onClose, onSaved, endpoint = '/t2m/outcome' }) {
  const { post }        = useApi();
  const { user }        = useAuth();
  const { currentClub } = useClub();
  const repName  = user?.name || user?.email || '[Rep Name]';
  const clubName = currentClub?.name || '[Club Name]';
  const isDenton = currentClub?.location_id === 'DEN';
  const PLAN_TYPES = isDenton ? PLAN_TYPES_DEN : PLAN_TYPES_BASE;

  // Determine default stage based on trial state
  function getDefaultStage() {
    if (!trial.trialDay) return 'checkin';
    if (trial.trialDay >= 12) return 'post_trial';
    if (trial.trialDay >= 9)  return 'trial_ask';
    return 'checkin';
  }

  const [stage, setStage]         = useState(getDefaultStage());
  const [outcome, setOutcome]     = useState('');
  const [why, setWhy]             = useState('');
  const [planType, setPlanType]   = useState('');
  const [notes, setNotes]         = useState('');
  const [verify, setVerify]       = useState('');
  const [followUp, setFollowUp]   = useState({ date: '', time: '9:00 AM', note: '' });
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [contactMethods, setContactMethods] = useState([]);

  // ─── Sprint 19.7 — Log Note mode ─────────────────────────────────────────
  // Lets a rep log a freeform contact note without picking an outcome or
  // touching any existing follow-up. Writes to outcome_log only — see
  // logContactNote in queries.js. Independent of the outcome flow below.
  const [noteMode,          setNoteMode]          = useState(false);
  const [noteText,          setNoteText]          = useState('');
  const [noteContactMethods, setNoteContactMethods] = useState([]);
  const [noteSaving,        setNoteSaving]        = useState(false);
  const [noteError,         setNoteError]         = useState('');

  function toggleNoteContactMethod(method) {
    setNoteContactMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  }

  async function handleSaveNote() {
    setNoteError('');
    if (!noteText.trim()) { setNoteError('Please enter a note.'); return; }
    setNoteSaving(true);
    try {
      await post('/contact/note', {
        module: 't2m',
        refId: trial.id,
        notes: noteText,
        contactMethods: noteContactMethods,
      });
      onSaved(null);
    } catch (e) {
      setNoteError(e.message || 'Failed to save note.');
    } finally {
      setNoteSaving(false);
    }
  }

  function toggleContactMethod(method) {
    setContactMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  }

  const stageCfg  = STAGE_CONFIG[stage];
  const outcomeObj = stageCfg.outcomes.find(o => o.value === outcome) || null;

  const needsWhy         = outcomeObj?.whyRequired || false;
  const whyOptional      = outcomeObj?.whyOptional || false;
  const needsPlan        = outcomeObj?.needsPlan || false;
  const followUpRequired = outcomeObj?.followUpRequired || false;
  const noFollowUp       = outcomeObj ? !outcomeObj.followUpRequired : false;
  const isHardNo         = outcomeObj?.category === 'hard_no';
  const isConverted      = outcomeObj?.category === 'converted';
  const needsVerify      = trial.verificationStatus === 'pending';

  // Reset outcome when stage changes
  useEffect(() => {
    setOutcome('');
    setWhy('');
    setPlanType('');
    setFollowUp({ date: '', time: '9:00 AM', note: '' });
    setError(null);
  }, [stage]);

  // Auto-set follow-up for converted (retention check-in rule)
  useEffect(() => {
    if (isConverted && !followUp.date) {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      setFollowUp(prev => ({ ...prev, date: formatDateISO(d) }));
    }
  }, [isConverted]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    if (!outcome)                              { setError('Select an outcome'); return; }
    if (needsWhy && !why.trim())               { setError('Please enter a reason — this feeds Revenue Leakage reporting'); return; }
    if (needsPlan && !planType)                { setError('Select the plan sold'); return; }
    if (followUpRequired && !followUp.date)    { setError('A follow-up date is required for this outcome'); return; }

    setSaving(true);
    setError(null);
    try {
      await post(endpoint, {
        trialId:            trial.id,
        outcome,
        outcomeCategory:    outcomeObj?.category,
        outcomeStage:       stage,
        closeReason:        why || undefined,
        planType:           planType || undefined,
        notes,
        contactMethods:     contactMethods.length > 0 ? contactMethods : undefined,
        verificationMethod: verify || undefined,
        followUpDate:       followUp.date   || undefined,
        followUpTime:       followUp.time   || undefined,
        followUpNote:       followUp.note   || undefined,
      });
      onSaved(outcome);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', maxWidth: '96vw' }} onClick={e => e.stopPropagation()}>
      <div className="modal" style={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>

        {trial.movingTo && (
          <div style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.35)', borderRadius: 6, padding: '7px 10px', marginBottom: 12, fontSize: 12, color: '#f6cf4a' }}>
            Moving to <strong>{trial.movingTo}</strong> — log what happened. The card moves when you save the outcome.
          </div>
        )}

        {/* Header */}
        <div className="modal-title">🌱 Log Contact — <span style={{ color: '#facc15' }}>{trial.name}</span></div>
        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#666', marginBottom: 16 }}>
          <span>Day {trial.trialDay ?? '—'}</span>
          <span>{trial.visits ?? 0} visit{trial.visits !== 1 ? 's' : ''}</span>
          {trial.phone && <span>{trial.phone}</span>}
          {trial.atRisk && <span style={{ color: '#ef4444', fontWeight: 700 }}>⚠ AT RISK</span>}
        </div>

        {/* Sprint 19.7 — Log Note toggle */}
        <div style={{ marginBottom: 14 }}>
          <button
            onClick={() => setNoteMode(m => !m)}
            style={{
              fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
              border: `1px solid ${noteMode ? '#facc15' : 'var(--border)'}`,
              background: noteMode ? '#facc1515' : 'var(--surface-2)',
              color: noteMode ? '#facc15' : 'var(--text-muted)',
              fontWeight: noteMode ? 700 : 400,
            }}
          >
            📝 {noteMode ? 'Back to Log Outcome' : "Just log a note (keep existing follow-up)"}
          </button>
        </div>

        {noteMode ? (
          <div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Contact Method
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { value: 'call',      label: '📞 Call' },
                  { value: 'text',      label: '💬 Text' },
                  { value: 'email',     label: '📧 Email' },
                  { value: 'in-person', label: '🤝 In Person' },
                ].map(cm => {
                  const active = noteContactMethods.includes(cm.value);
                  return (
                    <button
                      key={cm.value}
                      onClick={() => toggleNoteContactMethod(cm.value)}
                      style={{
                        fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                        border: `1px solid ${active ? '#facc15' : 'var(--border)'}`,
                        background: active ? '#facc1515' : 'var(--surface-2)',
                        color: active ? '#facc15' : 'var(--text-muted)',
                        fontWeight: active ? 700 : 400,
                      }}
                    >
                      {cm.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Note *
              </div>
              <textarea
                rows={4}
                placeholder="What happened, what did they say..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                style={{ width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontSize: 13 }}
              />
              <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>
                Saved to member contact history · does not change trial status or follow-up
              </div>
            </div>

            {noteError && (
              <div style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0 0 0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>
                {noteError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveNote} disabled={noteSaving}>
                {noteSaving ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        ) : (
        <>
        {/* Script panel */}
        <ScriptPanel
          trialId={trial.id}
          trialDay={trial.trialDay}
          visits={trial.visits}
          memberName={trial.name}
          repName={repName}
          clubName={clubName}
        />

        {/* Stage selector */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Contact Method <span style={{ fontWeight: 400, textTransform: 'none', fontSize: 10, color: 'var(--text-muted)' }}>(select all that apply)</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            {[
              { value: 'call',      label: '📞 Call' },
              { value: 'text',      label: '💬 Text' },
              { value: 'email',     label: '📧 Email' },
              { value: 'in-person', label: '🤝 In Person' },
            ].map(cm => {
              const active = contactMethods.includes(cm.value);
              return (
                <button
                  key={cm.value}
                  onClick={() => toggleContactMethod(cm.value)}
                  style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                    border: `1px solid ${active ? '#facc15' : '#333'}`,
                    background: active ? '#facc1515' : '#111',
                    color: active ? '#facc15' : '#666',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {cm.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Stage selector */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            What type of contact is this?
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.entries(STAGE_CONFIG).map(([key, cfg]) => {
              const active = stage === key;
              return (
                <button
                  key={key}
                  onClick={() => setStage(key)}
                  style={{
                    fontSize: 11, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${active ? cfg.color : '#333'}`,
                    background: active ? cfg.color + '20' : '#111',
                    color: active ? cfg.color : '#666',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {cfg.label}
                  <span style={{ marginLeft: 5, fontSize: 9, opacity: 0.7 }}>{cfg.sublabel}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Outcome pills */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Outcome *
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {stageCfg.outcomes.map(o => {
              const clr    = OUTCOME_CATEGORY_COLORS[o.category] || OUTCOME_CATEGORY_COLORS.interested;
              const active = outcome === o.value;
              return (
                <button
                  key={o.value}
                  onClick={() => { setOutcome(o.value); setWhy(''); setPlanType(''); setFollowUp({ date: '', time: '9:00 AM', note: '' }); }}
                  style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                    border: `1px solid ${active ? clr.border : '#333'}`,
                    background: active ? clr.bg : '#111',
                    color: active ? clr.text : '#777',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {o.value}
                  {o.whyRequired && <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.7 }}>+ Why</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Why field — required for Soft No / Hard No */}
        {(needsWhy || whyOptional) && outcome && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: needsWhy ? '#ef4444' : '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Why? {needsWhy ? '(required — feeds Revenue Leakage reporting)' : '(optional)'}
            </div>
            <input
              type="text"
              value={why}
              onChange={e => setWhy(e.target.value)}
              placeholder={WHY_PLACEHOLDERS[outcomeObj?.category] || WHY_PLACEHOLDERS.default}
              style={{
                width: '100%', background: '#111',
                border: `1px solid ${needsWhy ? '#ef444466' : '#333'}`,
                color: '#ccc', borderRadius: 5, padding: '7px 8px',
                fontSize: 12, boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {/* Plan type — when Converted */}
        {needsPlan && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Plan Sold *
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PLAN_TYPES.map(p => (
                <button
                  key={p}
                  onClick={() => setPlanType(p)}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                    border: `1px solid ${planType === p ? '#22c55e' : '#333'}`,
                    background: planType === p ? '#22c55e15' : '#111',
                    color: planType === p ? '#22c55e' : '#777',
                    fontWeight: planType === p ? 700 : 400,
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Notes (optional)</div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Additional context for next rep..."
            style={{
              width: '100%', background: '#111', border: '1px solid #333',
              color: '#ccc', borderRadius: 5, padding: '7px 8px', fontSize: 12,
              resize: 'none', height: 52, fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Verification — when pending */}
        {needsVerify && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              ⚠ Verification Pending — complete if possible
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {VERIFICATION_METHODS.map(m => (
                <button
                  key={m}
                  onClick={() => setVerify(verify === m ? '' : m)}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                    border: `1px solid ${verify === m ? '#f97316' : '#333'}`,
                    background: verify === m ? '#f9731615' : '#111',
                    color: verify === m ? '#f97316' : '#777',
                    fontWeight: verify === m ? 700 : 400,
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Follow-up scheduler */}
        {outcome && !isHardNo && (
          <div style={{ marginBottom: 16 }}>
            <FollowUpScheduler
              value={followUp}
              onChange={setFollowUp}
              required={followUpRequired}
              outcomeLabel={outcome}
            />
          </div>
        )}

        {/* Hard No — no follow-up notice */}
        {isHardNo && (
          <div style={{
            marginBottom: 16, padding: '8px 12px',
            background: '#ef444410', border: '1px solid #ef444430', borderRadius: 6,
            fontSize: 12, color: '#ef4444',
          }}>
            ⛔ Hard No closes this record — no follow-up will be scheduled. This outcome appears in Revenue Leakage reporting.
          </div>
        )}

        {error && (
          <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{error}</div>
        )}

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || !outcome || (needsPlan && !planType)}
            style={{ minWidth: 200 }}
          >
            {saving
              ? 'Saving...'
              : isHardNo
                ? '⛔ Close Record'
                : followUp.date
                  ? '📅 Save + Schedule Follow-Up'
                  : '✓ Save Contact'}
          </button>
        </div>
        </>
        )}
      </div>

      {/* Prior contacts across every module, right where the next one is logged (Josh, 10/6/26:
          "timeline is not coming over to new version"). Same shared component PB101 and Lead Engine use. */}
      {trial.memberId && (
        <div style={{ width: 260, flexShrink: 0, borderLeft: '1px solid var(--border)', paddingLeft: 16, maxHeight: '90vh', overflowY: 'auto' }}>
          <MemberTimeline memberId={trial.memberId} memberName={trial.name} compact maxHeight="calc(90vh - 80px)" />
        </div>
      )}
    </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  shell: {
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '12px 14px', marginBottom: 16,
  },
  header: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: 8,
  },
  scriptName:  { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  collapseBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, padding: 0 },
  whyPanel: {
    background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
    borderRadius: 6, padding: '8px 12px', marginBottom: 10,
  },
  whyLabel: { fontSize: 9, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 },
  whyText:  { fontSize: 12, color: 'var(--text)', lineHeight: 1.5 },
  body: {
    fontSize: 13, color: 'var(--text)', lineHeight: 1.6,
    padding: '8px 12px', background: 'var(--surface-3)',
    borderRadius: 6, marginBottom: 8, whiteSpace: 'pre-wrap',
  },
};
